import { create } from 'zustand';
import type { SelectedCategory } from '@/types';

interface ResponsesPanelState {
  isOpen: boolean;
  questionId: string | null;
  /**
   * Active category filter. Multi-select so researchers can look at
   * "Positive OR Negative" responses in one view, matching the pre-redesign
   * inline table. An empty array means "no filter — show everything".
   *
   * Shared between the inline chip filter above the Respondent Answers zone
   * and the slide-out ResponsesPanel — both views read/write the same
   * selection so they stay in sync regardless of which surface the user
   * interacts with.
   */
  selectedCategories: SelectedCategory[];
  open: (questionId: string, category?: SelectedCategory) => void;
  close: () => void;
  setSelectedCategories: (categories: SelectedCategory[]) => void;
  /** Clear just the category filter without closing the panel. */
  clearCategories: () => void;
  /**
   * Toggle a single category in/out of the filter without changing
   * `isOpen`. Used by the inline chart-bar click and inline chip row
   * so they update the filter without forcing the slide-out drawer
   * open. Switching to a different question resets the selection so
   * stale labels don't leak across questions.
   */
  toggleCategory: (questionId: string, category: SelectedCategory) => void;
  /** Reset the filter when the user navigates to a different question. */
  resetForQuestion: (questionId: string) => void;
}

const useResponsesPanelStore = create<ResponsesPanelState>((set) => ({
  isOpen: false,
  questionId: null,
  selectedCategories: [],
  open: (questionId, category) =>
    set((state) => {
      // If the panel is being re-opened for the SAME question that
      // already has a filter from the inline chip row, preserve that
      // selection — the panel is meant to be an "advanced view" of
      // the same filter, not a reset button. When no category is
      // passed (e.g. the "Search & sort" CTA) and the question
      // matches, keep the current `selectedCategories` rather than
      // wiping them. When a category IS passed (rare in the new
      // model — only used by callers that explicitly want a
      // single-category open), still set it as the sole selection.
      if (!category && state.questionId === questionId) {
        return { isOpen: true, questionId, selectedCategories: state.selectedCategories };
      }
      return {
        isOpen: true,
        questionId,
        selectedCategories: category ? [category] : [],
      };
    }),
  close: () => set({ isOpen: false }),
  setSelectedCategories: (categories) => set({ selectedCategories: categories }),
  clearCategories: () => set({ selectedCategories: [] }),
  toggleCategory: (questionId, category) =>
    set((state) => {
      // Different question → start a fresh single-item selection.
      // Prevents stale category labels from the previous question
      // leaking across when a user rapidly switches questions and
      // clicks a chart bar.
      if (state.questionId !== questionId) {
        return { questionId, selectedCategories: [category] };
      }
      const exists = state.selectedCategories.some((c) => c.label === category.label);
      return {
        questionId,
        selectedCategories: exists
          ? state.selectedCategories.filter((c) => c.label !== category.label)
          : [...state.selectedCategories, category],
      };
    }),
  resetForQuestion: (questionId) =>
    set((state) => {
      if (state.questionId === questionId) return state;
      return { ...state, questionId, selectedCategories: [] };
    }),
}));

export default useResponsesPanelStore;
